/*
  Shared helpers for the builder landing page sections (builder-*.liquid), loaded by snippets/builder-lp-assets.liquid.
  <blp-loop>   muted looping video over a still image; plays only while on screen, never with reduced motion.
  <blp-flap>   split-flap number that flips into its value when it scrolls into view.
  <blp-tip>    small mascot head that opens a tip bubble (a bottom sheet on phones).
  [data-blp-watch]  any button with a YouTube/Vimeo URL or YouTube id opens it in one shared video dialog.
  <blp-builder-modal>  opens the theme's builder modal (bos-builder-modal) for links to #build or ?rb=<preset>.
  Motion layer: sections rendered with data-blp-motion get scroll reveals, struck-in headings, mascot pops,
  drawn numerals, the "Power up!" flash and embers. Off with prefers-reduced-motion and when the theme's
  animations setting is disabled (the section then doesn't output data-blp-motion).
*/
window.blpReduce = window.blpReduce || window.matchMedia('(prefers-reduced-motion: reduce)');

window.blpOnView = window.blpOnView || function blpOnView(el, cb, options = { threshold: 0.4 }) {
  if (!('IntersectionObserver' in window)) { cb(true); return null; }
  const io = new IntersectionObserver((entries) => entries.forEach((e) => cb(e.isIntersecting)), options);
  io.observe(el);
  return io;
};

if (!customElements.get('blp-loop')) {
  customElements.define('blp-loop', class BlpLoop extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.video.setAttribute('aria-hidden', 'true');
      this.video.setAttribute('tabindex', '-1');
      this.video.removeAttribute('controls');
      this.video.addEventListener('playing', () => this.classList.add('is-playing'));
      if (window.blpReduce.matches) return;
      this.io = window.blpOnView(this, (inView) => {
        if (inView) this.video.play().catch(() => {});
        else this.video.pause();
      }, { threshold: 0.25 });
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
    }
  });
}

if (!customElements.get('blp-flap')) {
  customElements.define('blp-flap', class BlpFlap extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = '1';
      const value = this.dataset.value || this.textContent.trim();
      const chars = [...value].map((ch) => (/\d/.test(ch)
        ? `<span class="blp-flap" data-to="${ch}">${window.blpReduce.matches ? ch : '0'}</span>`
        : `<span class="blp-flap blp-flap--sym">${ch.replace(/</g, '&lt;')}</span>`)).join('');
      this.innerHTML = `<span class="visually-hidden">${value.replace(/</g, '&lt;')}</span><span class="blp-flap-set" aria-hidden="true">${chars}</span>`;
      if (window.blpReduce.matches) return;
      this.io = window.blpOnView(this, (inView) => {
        if (!inView) return;
        this.io && this.io.disconnect();
        this.querySelectorAll('.blp-flap[data-to]').forEach((flap, i) => {
          const to = Number(flap.dataset.to);
          let n = 0;
          const step = () => {
            flap.classList.remove('is-flip');
            void flap.offsetWidth;
            flap.classList.add('is-flip');
            flap.textContent = n;
            if (n === to) return;
            n = (n + 1) % 10;
            setTimeout(step, 70);
          };
          setTimeout(step, i * 120);
        });
      });
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
    }
  });
}


/* ---------- Tips ---------- */
if (!customElements.get('blp-tip')) {
  customElements.define('blp-tip', class BlpTip extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.blp-tip__head');
      this.pop = this.querySelector('.blp-tip__pop');
      if (!this.button || !this.pop) return;
      this.onToggle = (e) => { e.preventDefault(); this.toggle(!this.classList.contains('is-open')); };
      this.onKey = (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) { this.toggle(false); this.button.focus(); } };
      this.onDoc = (e) => { if (!this.contains(e.target)) this.toggle(false); };
      this.button.addEventListener('click', this.onToggle);
      this.querySelector('.blp-tip__x')?.addEventListener('click', () => { this.toggle(false); this.button.focus(); });
      this.addEventListener('keydown', this.onKey);
    }

    toggle(open) {
      document.querySelectorAll('blp-tip.is-open').forEach((t) => { if (t !== this) t.toggle(false); });
      this.classList.toggle('is-open', open);
      this.pop.hidden = !open;
      this.button.setAttribute('aria-expanded', String(open));
      if (open) {
        // flip the bubble when it would run off the right edge
        this.pop.classList.remove('is-left');
        const r = this.pop.getBoundingClientRect();
        if (r.right > window.innerWidth - 8) this.pop.classList.add('is-left');
        setTimeout(() => document.addEventListener('click', this.onDoc), 0);
      } else {
        document.removeEventListener('click', this.onDoc);
      }
    }

    disconnectedCallback() { document.removeEventListener('click', this.onDoc); }
  });
}

/* ---------- Shared video dialog ---------- */
window.blpVideoEmbed = window.blpVideoEmbed || function blpVideoEmbed(input) {
  const value = String(input || '').trim();
  if (!value) return null;
  const vimeo = value.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
  let id = value;
  let start = 0;
  try {
    if (/^https?:/.test(value)) {
      const url = new URL(value);
      start = parseInt(url.searchParams.get('t') || url.searchParams.get('start') || '0', 10) || 0;
      if (url.hostname.includes('youtu.be')) id = url.pathname.slice(1);
      else if (url.searchParams.get('v')) id = url.searchParams.get('v');
      else id = url.pathname.split('/').filter(Boolean).pop();
    }
  } catch (e) { /* not a URL: treat as an id */ }
  if (!/^[\w-]{6,20}$/.test(id)) return null;
  return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1${start ? `&start=${start}` : ''}`;
};

if (!window.blpWatchReady) {
  window.blpWatchReady = true;
  let dialog;
  let opener;
  const close = () => { if (dialog && dialog.open) dialog.close(); };
  const build = (closeLabel) => {
    dialog = document.createElement('dialog');
    dialog.className = 'blp blp-dialog blp-watch';
    dialog.innerHTML = `<div class="blp-watch__in"><div class="blp-watch__frame"></div><button type="button" class="blp-watch__close"></button></div>`;
    dialog.querySelector('.blp-watch__close').textContent = closeLabel || 'Close';
    dialog.addEventListener('click', (e) => { if (e.target === dialog || e.target.closest('.blp-watch__close')) close(); });
    dialog.addEventListener('close', () => {
      dialog.querySelector('.blp-watch__frame').innerHTML = '';
      if (opener) opener.focus();
    });
    document.body.appendChild(dialog);
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-blp-watch]');
    if (!btn) return;
    const src = window.blpVideoEmbed(btn.dataset.blpWatch);
    if (!src || typeof HTMLDialogElement === 'undefined') return; // a link fallback (href) keeps working
    e.preventDefault();
    if (!dialog) build(btn.dataset.blpWatchClose);
    opener = btn;
    const frame = document.createElement('iframe');
    frame.src = src;
    frame.title = btn.dataset.blpWatchTitle || btn.getAttribute('aria-label') || 'Video';
    frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    frame.allowFullscreen = true;
    dialog.setAttribute('aria-label', frame.title);
    dialog.querySelector('.blp-watch__frame').replaceChildren(frame);
    dialog.showModal();
  });
}

/* ---------- Motion layer ---------- */
window.blpMotion = window.blpMotion || (() => {
  const still = () => window.blpReduce.matches;
  let io;
  const enter = (el) => { el.classList.add('is-in'); (el.blpOnIn || []).forEach((fn) => fn()); el.blpOnIn = []; };
  const watch = (el, fn) => {
    if (fn) (el.blpOnIn = el.blpOnIn || []).push(fn);
    if (!('IntersectionObserver' in window)) { enter(el); return; }
    io = io || new IntersectionObserver((entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      enter(e.target);
    }), { threshold: 0, rootMargin: '0px 0px -10% 0px' });
    io.observe(el);
  };

  // Each letter of a heading is struck in; the real text stays for screen readers
  const forge = (h) => {
    if (h.dataset.blpForged) return;
    h.dataset.blpForged = '1';
    const text = h.textContent.trim();
    if (!text) return;
    let i = 0;
    const words = text.split(/\s+/).map((w) => `<span class="blp-forge__word">${[...w].map((c) => `<span class="blp-forge__ch" style="--i:${i++}">${c.replace(/[&<>]/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[m]))}</span>`).join('')}</span>`);
    h.innerHTML = `<span class="visually-hidden">${h.innerHTML}</span><span aria-hidden="true">${words.join(' ')}</span>`;
    watch(h);
  };

  // Outline numeral that draws itself in
  const numeral = (el) => {
    if (el.dataset.blpNumDone) return;
    el.dataset.blpNumDone = '1';
    const n = el.textContent.trim();
    el.innerHTML = `<svg class="blp-num" viewBox="0 0 220 140" aria-hidden="true" focusable="false"><text x="2" y="122">${n}</text></svg>`;
    watch(el);
  };

  // Embers floating up behind dark sections (canvas, runs only while on screen)
  const embers = (host, max) => {
    if (host.querySelector(':scope > .blp-embers')) return;
    const cv = document.createElement('canvas');
    cv.className = 'blp-embers';
    cv.setAttribute('aria-hidden', 'true');
    host.prepend(cv);
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    let w = 0; let h = 0; let parts = []; let running = false; let last = 0;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const size = () => { w = cv.clientWidth; h = cv.clientHeight; cv.width = w * dpr; cv.height = h * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
    const spawn = (burst) => ({ x: Math.random() * w, y: burst ? h * (0.4 + Math.random() * 0.6) : h + 10, vx: (Math.random() - 0.5) * 0.3,
      vy: -(0.3 + Math.random() * 0.9), r: 0.6 + Math.random() * 1.6, life: 0, max: 240 + Math.random() * 300, flick: Math.random() * 6.28 });
    size();
    window.addEventListener('resize', size, { passive: true });
    const count = Math.round(Math.min(max, w / 16));
    for (let k = 0; k < count; k++) parts.push(spawn(true));
    const tick = (t) => {
      if (!running || !cv.isConnected) return;
      const dt = Math.min(2.5, (t - last) / 16.7 || 1);
      last = t;
      ctx.clearRect(0, 0, w, h);
      parts = parts.map((p) => {
        p.life += dt; p.flick += 0.08 * dt; p.x += (p.vx + Math.sin(p.flick) * 0.22) * dt; p.y += p.vy * dt;
        const k = 1 - p.life / p.max;
        if (k <= 0 || p.y < -10) return spawn(false);
        const a = Math.max(0, k) * (0.5 + 0.45 * Math.sin(p.flick * 3));
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4);
        g.addColorStop(0, `rgba(255,214,170,${a})`);
        g.addColorStop(0.35, `rgba(255,75,41,${a * 0.8})`);
        g.addColorStop(1, 'rgba(255,75,41,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 4, 0, 6.283);
        ctx.fill();
        return p;
      });
      requestAnimationFrame(tick);
    };
    window.blpOnView(cv, (inView) => {
      const was = running;
      running = inView;
      if (running && !was) { if (!w) size(); requestAnimationFrame(tick); }
    }, { threshold: 0 });
  };

  // "Power up!" flash when a card's link is used
  const power = (card) => {
    if (card.dataset.blpPowerReady) return;
    card.dataset.blpPowerReady = '1';
    const fx = document.createElement('span');
    fx.className = 'blp-pow';
    fx.setAttribute('aria-hidden', 'true');
    fx.textContent = card.dataset.blpPower || 'Power up!';
    card.appendChild(fx);
    card.addEventListener('click', (e) => {
      if (!e.target.closest('a, button')) return;
      card.classList.remove('is-powered');
      void card.offsetWidth;
      card.classList.add('is-powered');
      setTimeout(() => card.classList.remove('is-powered'), 900);
    });
  };

  const init = (root) => {
    const sections = root.matches && root.matches('[data-blp-motion]') ? [root] : [...root.querySelectorAll('[data-blp-motion]')];
    sections.forEach((sec) => {
      if (still()) return;
      sec.classList.add('blp-motion-on');
      sec.querySelectorAll('[data-blp-stagger]').forEach((list) => {
        [...list.children].forEach((child, i) => child.style.setProperty('--k', i % 6));
      });
      sec.querySelectorAll('[data-blp-reveal], [data-blp-pop]').forEach((el, i) => {
        if (el.hasAttribute('data-blp-pop')) el.style.setProperty('--d', `${(i % 4) * 90}ms`);
        watch(el);
      });
      sec.querySelectorAll('[data-blp-forge]').forEach(forge);
      sec.querySelectorAll('[data-blp-num]').forEach(numeral);
      sec.querySelectorAll('[data-blp-power]').forEach(power);
      if (sec.hasAttribute('data-blp-embers') && sec.classList.contains('blp-section--ink')) embers(sec, Number(sec.dataset.blpEmbers) || 70);
      sec.querySelectorAll('[data-blp-embers-host]').forEach((host) => embers(host, 18));
      // Background or automation tabs never fire IntersectionObserver: show the end state
      setTimeout(() => { if (document.hidden) sec.querySelectorAll('[data-blp-reveal], [data-blp-pop], [data-blp-forge], [data-blp-num]').forEach(enter); }, 2500);
    });
  };

  const start = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  return { init };
})();


/* ---------- Builder opened in place (sections/builder-modal.liquid) ---------- */
if (!customElements.get('blp-builder-modal')) {
  customElements.define('blp-builder-modal', class BlpBuilderModal extends HTMLElement {
    connectedCallback() {
      this.anchor = this.dataset.anchor || 'build';
      this.pushed = false;
      this.wasOpen = false;

      this.onClick = (e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const link = e.target.closest('a[href]');
        if (!link || link.target === '_blank') return;
        const url = new URL(link.getAttribute('href'), window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !this.wants(url)) return;
        e.preventDefault();
        this.openFrom(url);
      };
      this.onPop = () => {
        if (this.wants(new URL(window.location.href))) {
          this.builder()?.open();
        } else if (document.body.classList.contains('bos-modal-open')) {
          this.pushed = false;
          this.builder()?.close();
        }
      };
      document.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPop);

      // Clean the URL when the builder is closed with its own Back button
      this.observer = new MutationObserver(() => {
        const open = document.body.classList.contains('bos-modal-open');
        if (this.wasOpen && !open) this.closed();
        this.wasOpen = open;
      });
      this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      if (this.hasAttribute('data-open-on-load') && this.wants(new URL(window.location.href))) {
        customElements.whenDefined('bos-builder-modal').then(() => requestAnimationFrame(() => this.open()));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPop);
      if (this.observer) this.observer.disconnect();
    }

    builder() {
      return this.querySelector('bos-builder-modal');
    }

    wants(url) {
      return url.hash === `#${this.anchor}` || url.searchParams.has('rb');
    }

    openFrom(url) {
      // Keep this page's params (view, variant), take the link's preset
      const next = new URL(window.location.href);
      next.searchParams.delete('rb');
      url.searchParams.forEach((value, key) => next.searchParams.set(key, value));
      next.hash = `#${this.anchor}`;
      const rb = next.searchParams.get('rb');
      // The builder reads the preset once, when it first loads. A different preset after that needs a fresh page.
      if (window.BOSBuilder && rb && rb !== this.loadedRb) {
        window.location.assign(next.href);
        return;
      }
      if (next.href !== window.location.href) {
        window.history.pushState({ blpBuilder: true }, '', next.href);
        this.pushed = true;
      }
      this.open();
    }

    open() {
      const builder = this.builder();
      if (!builder || typeof builder.open !== 'function') return;
      if (this.loadedRb === undefined) this.loadedRb = new URL(window.location.href).searchParams.get('rb');
      builder.open();
    }

    closed() {
      if (this.pushed) {
        this.pushed = false;
        window.history.back();
        return;
      }
      const url = new URL(window.location.href);
      if (!this.wants(url)) return;
      url.searchParams.delete('rb');
      url.hash = '';
      window.history.replaceState(window.history.state, '', url.href);
    }
  });
}
